import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const Alerts = ({
  title,
  children,
  onClose,
  isXVisible = true,
  width = "max-w-2xl",
  position = 'center', // 'center' (default) or 'top-right'
  autoClose = false, // boolean: if true, auto close after autoCloseMs
  autoCloseMs = 30000, // default 30 seconds
  pauseOnHover = true // if true, hovering pauses the auto-close timer
}) => {
  const isTopRight = position === 'top-right';
  const timerRef = useRef(null);

  useEffect(() => {
    // Setup auto-close timer when requested
    if (autoClose && typeof onClose === 'function') {
      timerRef.current = setTimeout(() => {
        onClose();
      }, autoCloseMs);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoClose, autoCloseMs, onClose]);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const restartTimer = () => {
    clearTimer();
    if (autoClose && typeof onClose === 'function') {
      timerRef.current = setTimeout(() => onClose(), autoCloseMs);
    }
  };

  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  const renderCenteredModal = () => (
    <div className="fixed inset-0 z-[20000] flex items-center justify-center bg-[var(--app-overlay)] p-4 modal-fade-in" role="dialog" aria-modal="true">
      <div
        className={`bg-surface rounded-lg shadow-xl ${width} w-full max-h-[90vh] overflow-y-auto transition transform modal-scale-in`}
        onMouseEnter={() => { if (pauseOnHover) clearTimer(); }}
        onMouseLeave={() => { if (pauseOnHover) restartTimer(); }}
      >
        <div className="flex items-center justify-between border-b border-line p-6">
          <h2 className="flex items-center text-2xl font-semibold text-content">
            {title}
          </h2>
          {isXVisible && (
            <button
              className="text-content transition-colors hover:text-content-muted"
              onClick={onClose}
              type="button"
              aria-label="Cerrar"
            >
              <span className="text-2xl">×</span>
            </button>
          )}
        </div>
        <div className="space-y-4 p-6 text-content">
          {children}
        </div>
      </div>
    </div>
  );

  const renderTopRightAlert = () => (
    <div
      className="fixed right-4 top-4 z-[20001] p-2 alert-slide-in"
      role="status"
      aria-live="polite"
      onMouseEnter={() => { if (pauseOnHover) clearTimer(); }}
      onMouseLeave={() => { if (pauseOnHover) restartTimer(); }}
    >
      <div className={`bg-surface rounded-lg shadow-xl ${width} w-auto max-h-[80vh] overflow-y-auto transition transform`}>
        <div className="flex items-center justify-between border-b border-line p-4">
          <h3 className="mr-4 truncate text-2xl font-semibold text-content">{title}</h3>
          {isXVisible && (
            <button
              className="ml-2 text-content transition-colors hover:text-content-muted"
              onClick={onClose}
              type="button"
              aria-label="Cerrar"
            >
              <span className="text-2xl">×</span>
            </button>
          )}
        </div>
        <div className="p-4 text-sm text-content">
          {children}
        </div>
      </div>
    </div>
  );

  // Centered modal with overlay (default)
  if (!isTopRight) {
    return portalTarget ? createPortal(renderCenteredModal(), portalTarget) : renderCenteredModal();
  }

  // Top-right non-modal alert (no overlay)
  return portalTarget ? createPortal(renderTopRightAlert(), portalTarget) : renderTopRightAlert();
};

export default Alerts;